import { useMemo } from 'react';

import dayjs from 'dayjs';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { EndType, WidgetInputs } from '../@types/types';
import { useFeature } from '../featureFlags';
import { GiftType } from '../shared/constants';

export function useWatchedSelections(
	watch: UseFormWatch<WidgetInputs>,
	setValue: UseFormSetValue<WidgetInputs>,
	{ hasApplePay }: { hasApplePay: boolean }
) {
	const isApplePayEnabled = useFeature('ApplePay');
	const [
		selectedGiftType,
		selectedEndType,
		selectedRecurringStartDate,
		selectedRecurringEndDate,
		selectedRecurrence,
		selectedGiftDate,
		selectedMultiFunds,
	] = watch([
		'GiftType',
		'RecurringEndType',
		'RecurringStartDate',
		'RecurringEndDate',
		'Recurrence',
		'GiftDate',
		'MultiFunds',
	]);

	return useMemo(() => {
		const data = {
			isOneTime: selectedGiftType === GiftType.ONE_TIME.toString(),
			isRecurring: selectedGiftType === GiftType.RECURRING.toString(),
			giftType: selectedGiftType === GiftType.ONE_TIME.toString() ? GiftType.ONE_TIME : GiftType.RECURRING,
			endType: null as EndType | null,
			recurringStartDate: selectedRecurringStartDate,
			recurringEndDate: selectedRecurringEndDate,
			recurrence: selectedRecurrence,
			giftDate: selectedGiftDate,
			giftDateIsToday: dayjs().isSame(selectedGiftDate, 'day'),
			recurringStartDateIsToday: dayjs().isSame(selectedRecurringStartDate, 'day'),
			multiFunds: selectedMultiFunds,
			showApplePay: false,
		};

		data.showApplePay =
			isApplePayEnabled && hasApplePay && !data.isRecurring && dayjs().isSame(data.giftDate, 'day');
		switch (selectedEndType) {
			case EndType.Occurrences.toString():
				setValue('RecurringEndDate', undefined);
				return { ...data, endType: EndType.Occurrences };
			case EndType.Date.toString():
				setValue('RecurringEndOccurence', undefined);
				return { ...data, endType: EndType.Date };
			default:
				setValue('RecurringEndOccurence', undefined);
				setValue('RecurringEndDate', undefined);
				return { ...data, endType: EndType.Never };
		}
	}, [
		selectedGiftType,
		selectedRecurringStartDate,
		selectedRecurringEndDate,
		selectedRecurrence,
		selectedGiftDate,
		isApplePayEnabled,
		selectedEndType,
		selectedMultiFunds,
		hasApplePay,
		setValue,
	]);
}
